import { useEffect, useState } from 'preact/hooks';

export type ScriptStatus = 'loading' | 'ready' | 'error';

/**
 * 같은 src 를 여러 컴포넌트가 요청해도 <script> 는 한 번만 붙인다.
 * 구 프로젝트의 훅은 "엘리먼트가 있으면 로드 완료"로 판단해서, 아직 내려받는
 * 중인 스크립트를 ready 로 오인하는 경쟁 상태가 있었다. 상태를 여기서 들고 간다.
 */
const registry = new Map<
  string,
  { status: ScriptStatus; listeners: Set<(s: ScriptStatus) => void> }
>();

function notify(src: string, status: ScriptStatus) {
  const entry = registry.get(src);
  if (!entry) return;

  entry.status = status;
  for (const listener of entry.listeners) listener(status);
}

/**
 * 외부 SDK 스크립트를 동적으로 로드한다.
 *
 * 구 프로젝트는 실패와 로딩중을 모두 `false` 로 돌려줘서 호출부가 영원히
 * 기다렸다. 여기서는 'loading' / 'ready' / 'error' 를 구분해 돌려준다.
 *
 * @param src   스크립트 주소 (이 값이 캐시 키다)
 * @param attrs script 엘리먼트에 얹을 추가 속성 (integrity, crossorigin 등)
 */
export function useExternalScript(src: string, attrs?: Record<string, string>): ScriptStatus {
  const [status, setStatus] = useState<ScriptStatus>(() => registry.get(src)?.status ?? 'loading');

  useEffect(() => {
    let entry = registry.get(src);

    if (!entry) {
      entry = { status: 'loading', listeners: new Set() };
      registry.set(src, entry);

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      for (const [key, value] of Object.entries(attrs ?? {})) script.setAttribute(key, value);

      script.addEventListener('load', () => notify(src, 'ready'));
      script.addEventListener('error', () => notify(src, 'error'));
      document.head.appendChild(script);
    }

    setStatus(entry.status);
    entry.listeners.add(setStatus);

    return () => {
      registry.get(src)?.listeners.delete(setStatus);
    };
    // attrs 는 매 렌더 새 객체가 되기 쉬워 의존성에서 뺀다. src 가 캐시 키다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return status;
}

/** 구글 Identity Services (GIS) */
export const useGoogleSdk = () => useExternalScript('https://accounts.google.com/gsi/client');

/** 네이버 아이디로 로그인 SDK */
export const useNaverSdk = () =>
  useExternalScript('https://static.nid.naver.com/js/naveridlogin_js_sdk_2.0.2-nopolyfill.js');
